import { useEffect, useState } from 'react';
import { useRemoteSearchOptions, type SearchOption } from './use-remote-search-options';

/**
 * `useRemoteSearchOptions` for a multiple Select. A chosen record the current results don't hold (saved earlier, or left behind by a new
 * search) is fetched one by one and kept, so a tag shows its label rather than its id.
 *
 * `fetchOptions` must be stable (`useCallback`); `fetchOne` is read once per unknown id.
 */
export function useRemoteMultiSearchOptions<T extends SearchOption>(
  fetchOptions: (q: string | undefined) => Promise<T[]>,
  fetchOne: (id: string) => Promise<T>,
  selected: string[] = [],
) {
  const { selectProps } = useRemoteSearchOptions(fetchOptions);
  const [fetched, setFetched] = useState<ReadonlyMap<string, T>>(new Map());
  const options = selectProps.options as T[];

  const missingKey = selected.filter((id) => !fetched.has(id) && !options.some((option) => option.value === id)).join(',');
  useEffect(() => {
    let cancelled = false;
    const load = (id: string) =>
      fetchOne(id)
        .catch((): T => ({ value: id, label: id }) as T)
        .then((option) => !cancelled && setFetched((current) => new Map(current).set(id, option)));
    for (const id of missingKey ? missingKey.split(',') : []) void load(id);
    return () => {
      cancelled = true;
    };
  }, [missingKey, fetchOne]);

  const chosenOutsideResults = selected.flatMap((id) => (fetched.has(id) && !options.some((option) => option.value === id) ? [fetched.get(id) as T] : []));
  return { ...selectProps, options: [...chosenOutsideResults, ...options] };
}
